import React, { useState, useEffect, useMemo } from 'react';
import { FlatList, Platform, RefreshControl, ScrollView, TouchableOpacity, View } from 'react-native'

import api from "../../../../utils/api"
import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import IcomoonIcon from '../../../../components/IcomoonIcon';
import { Icon } from '../../../../components/Icon';
import { useDispatch } from 'react-redux';
import { actions } from '../../../../store/Reducers/auth';
import { useQuery } from '@tanstack/react-query';
import AddTagModal from './AddTagModal';
import EntriesLog from './EntriesLog';
import TagDetailBottomSheet from './TagDetailBottomSheet';
import CamerasGrid from './CamerasGrid';
import Header from '../../../../navigation/Header';
import { Image } from 'expo-image';
import IconButton from '../../../../components/IconButton';
import AccessControlPanelSettingsModal from './AccessControlPanelSettings';
import TabsSelector from '../../../../components/TabsSelector';
import Toggle from '../../../../components/Toggle';
import SearchInput from '../../../../components/SearchInput';
import { Illustration } from '../../../../components/Illustration';
import ChevronChico from '../../../../components/ChevronChico';
import useActiveAdminCommunity from '../../../../hooks/useActiveAdminCommunity';
import { hasFeature } from '../../../../utils/features';
import { Ionicons } from '@expo/vector-icons';







export function TagsList({ communityId, hasOfflinePanels, onOpenPanelSettings }: any){
  const isWeb = Platform.OS === 'web'
  const [showAddTagModal, setShowAddTagModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState();
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [unassignedOnly, setUnassignedOnly] = useState(false);

  const { data: tags, refetch: refetchTags, isLoading: isLoadingTags } = useQuery({
    queryKey: ['community.tags', communityId],
    queryFn: () => api.get(`/communities/${communityId}/access/tags?with=units`).then(response => response.data),
  })

  const { data: panels } = useQuery({
    queryKey: ['community.control-panels', communityId],
    queryFn: () => api.get(`/communities/${communityId}/access/control-panels`).then(response => response.data),
  })

  const filteredTags = useMemo(() => {
    if (!Array.isArray(tags)) return tags

    return tags.filter(el => {
      if (typeFilter !== "all" && el.type !== typeFilter) return false
      if (unassignedOnly && el.unit) return false

      if (searchQuery == "") return true

      const regex = new RegExp(searchQuery, "i")
      const nameMatches = el.name?.match(regex)
      const codeMatches = el.code?.match(regex)
      const unitMatches = el.unit?.name?.match(regex)

      return nameMatches || unitMatches || codeMatches
    })
  }, [searchQuery, typeFilter, unassignedOnly, tags])

  // Resumen derivado del listado real (columna izquierda en web).
  const summary = useMemo(() => {
    const list = Array.isArray(tags) ? tags : []
    return {
      active: list.filter((t: any) => t.active).length,
      assigned: list.filter((t: any) => t.unit).length,
      unassigned: list.filter((t: any) => !t.unit).length,
      deactivated: list.filter((t: any) => !t.active).length,
    }
  }, [tags])

  const typeOptions = [
    { label: "Todos", value: "all" },
    { label: "Vehicular", value: "vehicle_tag" },
    { label: "Llavero", value: "rfid_keychain" },
    { label: "Matrícula", value: "license_plate" },
  ]

  const renderTagRow = ({ item: tag }: any) => (
    <TouchableOpacity
      style={tw.style(`bg-white rounded-2xl border border-gray-200 row justify-between items-center px-4 py-3 mb-3`, isWeb ? `mx-0` : `mx-3`)}
      onPress={() => setSelectedTag(tag)}
    >
      <View style={tw`flex-1`}>
        <View style={tw`row`}>
          <View style={tw.style(`h-2.3 w-2.3 rounded-full border border-secondary mr-1.5`, tag.unit && `bg-secondary`)}></View>

          <Text style={tw`text-base text-secondary`}>{tag.name ?? 'Sin nombre'}</Text>

          {
            tag.type === "vehicle_tag"
              ? <Icon name="tagVehicular" size={16} style={tw`ml-2`} />
              : tag.type === "rfid_keychain"
                ? <Icon name="tagLlavero" size={16} style={tw`ml-2`} />
                : <Ionicons name="help-outline" size={16} color={tw.color('gray-500')} style={tw`ml-2`} />
          }
        </View>

        <View style={tw`row`}>
          <Text style={tw`text-gray-500`}>{tag.code} - </Text>
          {
            tag.unit ?
              <Text style={tw`text-gray-500`}>{tag.unit.name}</Text>
              :
              <Text style={tw`text-gray-400`}>Sin asignar</Text>
          }
        </View>
      </View>

      <Text style={tw.style(tag.active ? `text-primary` : `text-gray-400`)}>{tag.active ? 'Activo' : 'Inactivo'}</Text>
    </TouchableOpacity>
  )

  const modals = (
    <>
      {
        showAddTagModal &&
          <AddTagModal
            communityId={communityId}
            panel={panels?.[0]}
            onRequestClose={() => setShowAddTagModal(false)}
            onTagCreated={() => {
              setShowAddTagModal(false)
              refetchTags()
            }}
            onAccessTagScanned={(tag) => {
              setShowAddTagModal(false)
              setSelectedTag(tag)
            }}
          />
      }

      {
        selectedTag &&
          <TagDetailBottomSheet
            visible={true}
            communityId={communityId}
            initialUnitId={selectedTag.unit?.uuid}
            tag={selectedTag}
            onRequestClose={() => setSelectedTag(null)}
            onTagEdited={() => {
              refetchTags()
              setSelectedTag(null)
            }}
          />
      }
    </>
  )

  // ===== Web: dos columnas (resumen | tabs + buscador + listado) =====
  if (isWeb) {
    return (
      <View style={tw`flex-1 flex-row`}>
        {/* Columna izquierda: título + resumen de tags */}
        <View style={tw`w-[320px] mr-4`}>
          <View style={tw`bg-secondary rounded-2xl row items-center px-4 py-3 mb-3`}>
            <Icon name="navAccesos" variant="white" width={24} height={20} />
            <Text style={tw`text-white font-600 text-lg ml-3`}>Accesos</Text>
          </View>

          <View style={tw`bg-white rounded-2xl border border-gray-200 overflow-hidden`}>
            <View style={tw`px-4 py-3 border-b border-gray-200`}>
              <Text style={tw`text-gray-500 font-600`}>Resumen</Text>
            </View>
            <SummaryRow iconName="tagActivo" label="Activos" value={summary.active} />
            <SummaryRow iconName="tagAsignado" label="Asignados" value={summary.assigned} />
            <SummaryRow iconName="tagLibre" label="Sin asignar" value={summary.unassigned} />
            <SummaryRow iconName="tagDesactivado" label="Desactivados" value={summary.deactivated} last />
          </View>

          {/* Estado de los paneles de control (siempre visible) */}
          {
            !!panels?.length &&
              <View style={tw`bg-white rounded-2xl border border-gray-200 overflow-hidden mt-4`}>
                <View style={tw`px-4 py-3 border-b border-gray-200`}>
                  <Text style={tw`text-gray-500 font-600`}>Paneles de control</Text>
                </View>
                {
                  panels.map((panel: any, i: number) =>
                    <View
                      key={panel.uuid}
                      style={tw.style(`row items-center px-4 py-3`, i < panels.length - 1 && `border-b border-gray-200`)}
                    >
                      <View style={tw.style(`rounded-full h-2.5 w-2.5 mr-3`, panel.is_alive ? `bg-green-500` : `bg-red`)}></View>
                      <Text style={tw`text-secondary flex-1`} numberOfLines={1}>{panel.name}</Text>
                      <Text style={tw.style(`text-sm font-500`, panel.is_alive ? `text-green-600` : `text-red`)}>
                        {panel.is_alive ? 'En línea' : 'Desconectado'}
                      </Text>
                    </View>
                  )
                }
              </View>
          }
        </View>

        {/* Columna derecha: tabs + buscador + listado (fondo transparente) */}
        <View style={tw`flex-1 max-w-[650px]`}>
          {/* Banda morada: título + tabs + buscador */}
          <View style={tw`bg-secondary rounded-2xl overflow-hidden`}>
            <View style={tw`row items-center px-4 pt-3 pb-1`}>
              <Illustration name="tags" width={24} height={24} />
              <Text style={tw`text-white font-600 text-lg ml-3`}>Tags de acceso</Text>
            </View>

            <TabsSelector
              pills
              selectedValue={typeFilter}
              onChange={setTypeFilter}
              options={typeOptions}
            />

            <View style={tw`row items-center px-3 pb-3 pt-1`}>
              <View style={tw`flex-1`}>
                <SearchInput style={{ container: tw`mb-0` }} onChange={setSearchQuery} />
              </View>

              {
                onOpenPanelSettings &&
                  <TouchableOpacity
                    style={tw`ml-3 w-11 h-11 rounded-full bg-[#632584] items-center justify-center`}
                    onPress={onOpenPanelSettings}
                  >
                    <IcomoonIcon name='settings' color={tw.color('white')} size={18} />
                    {
                      hasOfflinePanels &&
                        <View style={tw`rounded-full h-2.5 w-2.5 bg-red absolute top-2 right-2`}></View>
                    }
                  </TouchableOpacity>
              }

              <IconButton
                title={"Agregar tag"}
                color='secondary'
                textColor='white'
                style={tw`ml-3 bg-[#632584]`}
                onPress={() => setShowAddTagModal(true)}
              />
            </View>
          </View>

          {/* Solo sin asignar (card) */}
          <View style={tw`bg-white border border-gray-200 rounded-2xl px-4 py-3 mt-3 row items-center justify-between`}>
            <Text style={tw`text-secondary font-500`}>Solo sin asignar</Text>
            <Toggle value={unassignedOnly} onValueChange={setUnassignedOnly} />
          </View>

          <FlatList
            data={filteredTags}
            style={tw`flex-1 mt-3`}
            contentContainerStyle={tw`pb-4`}
            refreshControl={
              <RefreshControl onRefresh={refetchTags} refreshing={isLoadingTags} />
            }
            renderItem={renderTagRow}
            keyExtractor={item => item.uuid}
          />
        </View>

        {modals}
      </View>
    )
  }

  // ===== Móvil =====
  return (
    <View style={tw`flex-1`}>
      <TabsSelector
        pills
        selectedValue={typeFilter}
        onChange={setTypeFilter}
        options={typeOptions}
      />

      <View style={tw`bg-secondary px-3 pt-1 pb-3`}>
        <SearchInput style={{ container: tw`mb-0` }} onChange={setSearchQuery} />
      </View>

      <View style={tw`bg-white border border-gray-200 rounded-2xl px-4 py-3 mx-3 mt-3 mb-3 row items-center justify-between`}>
        <Text style={tw`text-secondary font-500`}>Solo sin asignar</Text>
        <Toggle value={unassignedOnly} onValueChange={setUnassignedOnly} />
      </View>

      <FlatList
        data={filteredTags}
        contentContainerStyle={tw`pt-2 pb-24`}
        refreshControl={
          <RefreshControl onRefresh={refetchTags} refreshing={isLoadingTags} />
        }
        renderItem={renderTagRow}
        keyExtractor={item => item.uuid}
      />

      <IconButton
        title={"Agregar tag"}
        color='secondary'
        textColor='white'
        style={tw`absolute bottom-4 self-center`}
        onPress={() => setShowAddTagModal(true)}
      />

      {modals}
    </View>
  )
}










/** Fila del resumen de tags (icono + etiqueta + número). */
function SummaryRow({ iconName, label, value, last }: any) {
  return (
    <View style={tw.style(`row items-center px-4 py-3`, !last && `border-b border-gray-200`)}>
      <View style={tw`w-6 items-center mr-3`}>
        <Icon name={iconName} size={18} />
      </View>
      <Text style={tw`text-gray-500 flex-1`}>{label}</Text>
      <Text style={tw`text-secondary font-600`}>{(value ?? 0).toLocaleString()}</Text>
    </View>
  )
}

/** Card de acceso del dashboard: encabezado (icono grande + título + chevron) y cuerpo debajo. */
function DashCard({ icon, title, description, badge, onPress, children }: any) {
  return (
    <TouchableOpacity
      style={tw`bg-white rounded-2xl border border-gray-200 px-4 py-4 mb-4`}
      onPress={onPress}
    >
      <View style={tw`row items-center`}>
        {icon ? <View style={tw`mr-3`}>{icon}</View> : null}
        <Text style={tw`text-secondary font-600 text-base flex-1`}>{title}</Text>
        {badge}
        <ChevronChico />
      </View>
      {!!description && <Text style={tw`text-gray-500 text-sm mt-2`}>{description}</Text>}
      {children}
    </TouchableOpacity>
  )
}


export default function AccessControlScreen({ navigation, route }) {
  const { communityId, community } = useActiveAdminCommunity()

  
  const dispatch = useDispatch()
  const [selectedTab, setSelectedTab] = useState("tags");
  const [showPanelSettingsModal, setShowPanelSettingsModal] = useState(false);
  const isWeb = Platform.OS === 'web'

  const { data: panels } = useQuery({
    queryKey: ['community.control-panels', communityId],
    queryFn: () => api.get(`/communities/${communityId}/access/control-panels`).then(response => response.data),
  })

  const hasOfflinePanels = panels?.some(panel => !panel.is_alive)
  // Si no están activas, no aparecen en el dashboard: cámaras es módulo contratado
  // (features), autorizaciones lo activa el admin (settings).
  const camerasEnabled = hasFeature(community, 'argos')
  const authorizationsEnabled = community.settings?.access_authorization?.enabled

  // Resumen de tags para el dashboard móvil (derivado del listado real).
  const { data: tags } = useQuery({
    queryKey: ['community.tags', communityId],
    queryFn: () => api.get(`/communities/${communityId}/access/tags?with=units`).then(r => r.data),
    enabled: !isWeb,
  })

  const tagsSummary = useMemo(() => {
    const list = Array.isArray(tags) ? tags : []
    return {
      active: list.filter((t: any) => t.active).length,
      assigned: list.filter((t: any) => t.unit).length,
      unassigned: list.filter((t: any) => !t.unit).length,
      deactivated: list.filter((t: any) => !t.active).length,
    }
  }, [tags])

  // Conteo de autorizaciones pendientes (para el contador del dashboard móvil).
  const { data: authorizations } = useQuery({
    queryKey: ['access-authorizations.counts', communityId],
    queryFn: () => api.get(`/communities/${communityId}/access-authorizations`, { params: { per_page: 200 } }).then(r => r.data?.data ?? r.data),
    enabled: !isWeb,
  })
  const pendingAuthorizations = useMemo(() => (Array.isArray(authorizations) ? authorizations : []).filter((a: any) => a.status === 'pending').length, [authorizations])


  return (
    <View style={tw.style(`flex-1`, !isWeb && `bg-[#F3F2F3]`)}>
      {
        // En web el topbar + sidebar ya dan contexto; el engrane de paneles vive en la
        // barra de la columna derecha (ver TagsList). El header morado es solo móvil.
        !isWeb &&
          <Header style={tw`bg-secondary rounded-b-0 mb-0`}>
            <View style={tw`w-full row justify-between`}>
              {/* Row Bienvenido */}
              <View style={tw`row`}>
                <Icon name="navAccesos" variant="white" width={24} height={20} />

                <View style={tw``}>
                  <Text style={tw`text-white ml-3 text-lg`}>Accesos</Text>
                </View>
              </View>

              {/*
                Botón viviendas
              */}
              <TouchableOpacity style={tw`py-3 pl-6 pr-4`} onPress={() => setShowPanelSettingsModal(true)}>
                <IcomoonIcon name='settings' color={tw.color('white')} size={20} />
                {
                  hasOfflinePanels &&
                    <View style={tw`rounded-full h-2.5 w-2.5 bg-red absolute top-2 right-2`}></View>
                }
              </TouchableOpacity>
            </View>
          </Header>
      }

      {
        isWeb ? (
          // Web: esta pantalla es exclusiva de Tags de acceso (Eventos y Cámaras son páginas aparte).
          <TagsList
            communityId={communityId}
            hasOfflinePanels={hasOfflinePanels}
            onOpenPanelSettings={() => setShowPanelSettingsModal(true)}
          />
        ) : (
          // Móvil: dashboard de accesos (cada card abre su pantalla completa).
          <ScrollView style={tw`flex-1`} contentContainerStyle={tw`px-4 pt-4 pb-10`}>
            {/* Tags de acceso + resumen */}
            <DashCard
              icon={<Illustration name="tags" width={40} height={40} />}
              title="Tags de acceso"
              onPress={() => navigation.navigate('Admin.AccessControl.Tags')}
            >
              <View style={tw`bg-gray-50 border border-gray-200 rounded-xl mt-3`}>
                <SummaryRow iconName="tagActivo" label="Activos" value={tagsSummary.active} />
                <SummaryRow iconName="tagAsignado" label="Asignados" value={tagsSummary.assigned} />
                <SummaryRow iconName="tagLibre" label="Sin asignar" value={tagsSummary.unassigned} />
                <SummaryRow iconName="tagDesactivado" label="Desactivados" value={tagsSummary.deactivated} last />
              </View>
            </DashCard>

            {
              camerasEnabled &&
                <DashCard
                  title="Cámaras"
                  description="Ve en tiempo real lo que sucede dentro del fraccionamiento"
                  onPress={() => navigation.navigate('Admin.AccessControl.Cameras')}
                />
            }

            <DashCard
              title="Eventos"
              description="Actividad en las puertas de acceso"
              onPress={() => navigation.navigate('Admin.AccessControl.Events')}
            />

            {/* Autorizaciones para proveedores (solo web por ahora) */}
            {
              authorizationsEnabled &&
              <View style={tw`bg-white rounded-2xl border border-gray-200 px-4 py-4`}>
              <View style={tw`row items-center`}>
                <Illustration name="autorizacion" width={40} height={40} style={tw`mr-3`} />
                <Text style={tw`text-secondary font-600 text-base flex-1`}>Autorizaciones para proveedores</Text>
                {
                  pendingAuthorizations > 0 &&
                    <View style={tw`bg-red rounded-full min-w-5 h-5 px-1.5 items-center justify-center ml-2`}>
                      <Text style={tw`text-white text-xs font-600`}>{pendingAuthorizations}</Text>
                    </View>
                }
              </View>
              <Text style={tw`text-gray-500 text-sm mt-2`}>
                Autoriza o rechaza las solicitudes de tus residentes para acceso de proveedores.
              </Text>
              <Text style={tw`text-primary text-xs font-500 mt-1`}>Próximamente · por ahora solo disponible en web</Text>
            </View>
            }
          </ScrollView>
        )
      }

      {
        showPanelSettingsModal &&
          <AccessControlPanelSettingsModal
            communityId={communityId}
            onRequestClose={() => setShowPanelSettingsModal(false)}
          />
      }
    </View>
  );
}
